import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { NO_VALUE } from '@pipes';
import {
  GatewayCommissioningStore,
  getCountryFlag,
  GATEWAY_COMMISSIONING_DEVICE,
  TransferInstallationFormData,
} from '@store';

import { SupportedCountry } from '../../../../shared/models/supportedCountry.model';
import { SupportedCountriesService } from '../../../../shared/services/supported-countries.service';
import { AddressForm, AddressFormGroup } from '../transfer-installation.helper';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressFormComponent implements OnInit, OnDestroy {
  addressFormGroup: FormGroup<AddressFormGroup>;

  readonly NO_VALUE = NO_VALUE;
  readonly deviceType = GATEWAY_COMMISSIONING_DEVICE;
  private readonly emptySupportedCountry: SupportedCountry = {
    countryCode: null,
    languageCode: null,
    locale: null,
    country: null,
    language: null,
    regions: [],
  };

  selectedCountry: SupportedCountry = this.emptySupportedCountry;
  country: string;
  supportedCountries: SupportedCountry[];

  public getCountryFlag = getCountryFlag;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  @Input() formData: TransferInstallationFormData;
  @Output() addressDataChange: EventEmitter<AddressForm> = new EventEmitter<AddressForm>();

  constructor(
    public gatewayCommissioningStore: GatewayCommissioningStore,
    private supportedCountriesService: SupportedCountriesService
  ) {}

  ngOnInit(): void {
    this.supportedCountries = this.supportedCountriesService.getUniqueSupportedCountriesSortedAlphabetically();
    this.country = this.formData.address.country;

    this.addressFormGroup = new FormGroup<AddressFormGroup>({
      street: new FormControl<string>(this.formData.address.street, [Validators.required]),
      houseNumber: new FormControl<string>(this.formData.address.houseNumber, [
        Validators.required,
      ]),
      zip: new FormControl<string>(this.formData.address.zip, [Validators.required]),
      city: new FormControl<string>(this.formData.address.city, [Validators.required]),
      country: new FormControl<string>(this.formData.address.country, [Validators.required]),
      region: new FormControl<string>(this.formData.address.region),
    });

    this.emitFormData();
    this.setSelectedCountry();

    this.gatewayCommissioningStore.transferInstallationLoading$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((transferInstallationLoading) => {
        if (transferInstallationLoading) {
          this.addressFormGroup.disable();
        } else {
          this.addressFormGroup.enable();
        }
      });

    this.addressFormGroup.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((value) => {
      this.gatewayCommissioningStore.setFormData({
        address: {
          street: value.street,
          houseNumber: value.houseNumber,
          zip: value.zip,
          city: value.city,
          country: value.country,
          region: value.region || null,
        },
      });

      this.emitFormData();
    });
  }

  onCountryChanged(country: { value: string }): void {
    if (!country.value) {
      this.selectedCountry = this.emptySupportedCountry;
      this.country = null;

      return;
    }

    this.selectedCountry = this.supportedCountries.find(
      (supportedCountry: SupportedCountry) =>
        supportedCountry.countryCode.toLowerCase() === country.value.toLowerCase()
    );
    this.addressFormGroup.controls.country.setValue(this.selectedCountry.countryCode);
    this.emitFormData();
  }

  private emitFormData(): void {
    this.addressDataChange.emit({
      isAddressFormValid: this.addressFormGroup.valid,
    });
  }

  private setSelectedCountry(): void {
    if (this.supportedCountries && this.country) {
      const selectedCountryResults = this.supportedCountries.filter(
        (country: SupportedCountry) =>
          country.countryCode.toLowerCase() === this.country.toLowerCase()
      );

      if (selectedCountryResults && selectedCountryResults.length) {
        this.selectedCountry = selectedCountryResults[0];
      } else {
        this.selectedCountry = this.emptySupportedCountry;
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
